import type { AxiosInstance } from 'axios'
import { keycloak } from '@/auth/keycloak'

/**
 * The auth and tracing rules, in ONE place, for every client in this app.
 *
 * Extracted when the write client arrived. Two copies of "refresh before
 * attaching, then replay once on 401" drift, and the direction they drift in is
 * the one where somebody's token silently stops being refreshed on the client
 * that matters most.
 */
export function installAuthInterceptors(client: AxiosInstance): void {
  /**
   * Refresh BEFORE attaching, not after a 401.
   *
   * The product added this after a real incident on 2026-08-13: two endpoints
   * returned 401 `ExpiredSignature` in the same second, caused by a background
   * tab whose 20-second `setInterval` refresh the browser had throttled. A
   * monitoring wall IS a background tab by design, and here the access token
   * lives 900 s instead of 86,400 — which multiplies the exposure by 96.
   *
   * `updateToken(30)` is a no-op while the token has more than 30 s left, so the
   * cost is a comparison.
   */
  client.interceptors.request.use(async (config) => {
    try {
      await keycloak.updateToken(30)
    } catch {
      /* fall through: the 401 replay below is the real recovery */
    }
    if (keycloak.token) config.headers.Authorization = `Bearer ${keycloak.token}`
    // Both allowed AND exposed by the server's CORS layer — if it were only
    // allowed, the browser would drop it from the response and a staff bug
    // report would lose the one id that ties it to a CloudWatch line.
    config.headers.traceparent = newTraceparent()
    return config
  })

  /**
   * Replay once after a 401, then give up. The flag is what stops a loop.
   *
   * Safe on a write as well as a read, and the reason is specific rather than
   * general: a 401 is produced by the gate BEFORE any handler body runs, so the
   * replayed request cannot be a second application of anything. A 500 is not
   * replayed here, and that distinction is the whole of `retryTransientMutation`.
   */
  client.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error?.config as (typeof error)['config'] & { _replayed?: boolean }
      if (error?.response?.status !== 401 || !cfg || cfg._replayed) {
        return Promise.reject(error)
      }
      cfg._replayed = true
      try {
        await keycloak.updateToken(-1)
      } catch {
        return Promise.reject(error)
      }
      return client.request(cfg)
    },
  )
}

/** W3C traceparent: `00-<32 hex>-<16 hex>-01`. */
export function newTraceparent(): string {
  const hex = (n: number) =>
    Array.from(crypto.getRandomValues(new Uint8Array(n)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  return `00-${hex(16)}-${hex(8)}-01`
}
